import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages, X } from 'lucide-react';
import { useAppLocale } from '../../providers/LocaleProvider';
import { useWordHelpOptional } from '../../contexts/WordHelpContext';
import { TOUR_TARGET_FOCUS_EVENT } from '../../learnerTour/tourTargetEvents';
import { prefersReducedMotion } from '../../utils/subscriptionAccessibility';
import { StickyAudioBlockToggle } from './StickyAudioBlockToggle';

interface WordHelpBlockToggleProps {
  blockId: number;
  enabled: boolean;
  onToggle: () => void;
  dataTourId?: string;
  /** Highlights the whole word-help row. */
  wordHelpTourId?: string;
  /** Highlights the this-lesson / all-words switch. The row turns on when the tour focuses it. */
  scopeTourId?: string;
  className?: string;
}

export const WordHelpBlockToggle: React.FC<WordHelpBlockToggleProps> = ({
  blockId,
  enabled,
  onToggle,
  dataTourId,
  wordHelpTourId,
  scopeTourId,
  className = ''
}) => {
  const { t } = useTranslation('wordHelp');
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const help = useWordHelpOptional();
  const guideId = useId();
  const scopeRef = useRef<HTMLDivElement>(null);
  const [motionReady, setMotionReady] = useState(false);
  const [thumb, setThumb] = useState<{ x: number; width: number } | null>(null);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    const id = requestAnimationFrame(() => setMotionReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    if (!scopeTourId) return;
    const onFocus = (event: Event) => {
      const key = (event as CustomEvent<{ targetKey?: string }>).detail?.targetKey;
      if (key !== scopeTourId || enabled) return;
      onToggle();
    };
    window.addEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
    return () => window.removeEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
  }, [scopeTourId, enabled, onToggle]);

  const localLabel = help ? (help.scopeKind === 'book' ? t('thisBook') : t('thisLesson')) : '';
  const scope = help?.scopeFor(blockId) ?? 'local';

  useLayoutEffect(() => {
    const group = scopeRef.current;
    if (!group || !help) return;
    const measure = () => {
      const button = group.querySelector<HTMLButtonElement>(
        `[data-word-help-scope="${scope}"]`
      );
      if (!button || button.offsetWidth < 1) return;
      const next = { x: button.offsetLeft, width: button.offsetWidth };
      setThumb((current) =>
        current && current.x === next.x && current.width === next.width ? current : next
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(group);
    return () => observer.disconnect();
  }, [help, blockId, scope, enabled, localLabel, isRtl]);

  if (!help) {
    return (
      <StickyAudioBlockToggle
        enabled={enabled}
        onToggle={onToggle}
        {...(dataTourId ? { dataTourId } : {})}
        className={className}
      />
    );
  }

  const onScopeKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!enabled) return;
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft' && event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }
    event.preventDefault();
    const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
    const next = forward ? 'all' : 'local';
    help.setScope(blockId, next);
    scopeRef.current
      ?.querySelector<HTMLButtonElement>(`[data-word-help-scope="${next}"]`)
      ?.focus();
  };

  const rowClass = `word-help-row${enabled ? ' is-open' : ''}${motionReady ? ' is-motion' : ''}${className ? ` ${className}` : ''}`;
  const chipClass = `word-help-chip${help.loadingFor(blockId) ? ' is-busy' : ''}`;
  const rowTourId = wordHelpTourId || dataTourId;
  const chipTourId = wordHelpTourId && dataTourId ? dataTourId : undefined;

  return (
    <div
      className={rowClass}
      dir={direction}
      {...(rowTourId ? { 'data-tour-id': rowTourId } : {})}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={enabled}
        aria-expanded={enabled}
        aria-busy={enabled && help.loadingFor(blockId)}
        aria-label={enabled ? t('hide') : t('show')}
        tabIndex={enabled ? 0 : -1}
        aria-hidden={enabled ? undefined : true}
        title={t('label')}
        className={chipClass}
        {...(chipTourId ? { 'data-tour-id': chipTourId } : {})}
      >
        <Languages className="word-help-chip__icon" aria-hidden />
        <span className="sr-only">{t('label')}</span>
      </button>
      {enabled ? null : (
        <button
          type="button"
          onClick={onToggle}
          aria-pressed={false}
          aria-describedby={guideId}
          aria-label={t('show')}
          title={t('label')}
          className="word-help-row__hit"
        />
      )}
      <div className="word-help-front">
        <div className="word-help-guide-slot" aria-hidden={enabled || undefined}>
          <div className="word-help-guide-slot__clip">
            <p id={guideId} className="word-help-guide">
              {t('guide')}
            </p>
          </div>
        </div>
        <div className="word-help-scope-slot">
          <div className="word-help-scope-slot__clip">
            <div className="word-help-open-bar">
              {help.allowAllWords ? (
                <div
                  ref={scopeRef}
                  className="word-help-scope"
                  {...(scopeTourId ? { 'data-tour-id': scopeTourId } : {})}
                  role="radiogroup"
                  aria-label={t('label')}
                  aria-hidden={enabled ? undefined : true}
                  onKeyDown={onScopeKeyDown}
                >
                  {thumb ? (
                    <span
                      className="word-help-scope__thumb"
                      style={{ width: thumb.width, transform: `translate3d(${thumb.x}px, 0, 0)` }}
                      aria-hidden
                    />
                  ) : null}
                  <button
                    type="button"
                    role="radio"
                    data-word-help-scope="local"
                    aria-checked={scope === 'local'}
                    tabIndex={enabled && scope === 'local' ? 0 : -1}
                    className="word-help-scope__option"
                    onClick={() => help.setScope(blockId, 'local')}
                  >
                    {localLabel}
                  </button>
                  <button
                    type="button"
                    role="radio"
                    data-word-help-scope="all"
                    aria-checked={scope === 'all'}
                    tabIndex={enabled && scope === 'all' ? 0 : -1}
                    className="word-help-scope__option"
                    onClick={() => help.setScope(blockId, 'all')}
                  >
                    {t('allWords')}
                  </button>
                </div>
              ) : null}
              <button
                type="button"
                className="word-help-dismiss"
                onClick={onToggle}
                aria-label={t('hide')}
                tabIndex={enabled ? 0 : -1}
                aria-hidden={enabled ? undefined : true}
              >
                <X className="word-help-dismiss__icon" aria-hidden />
              </button>
            </div>
          </div>
        </div>
      </div>
      {help.loadingFor(blockId) ? (
        <span className="sr-only" role="status">
          {t('searching')}
        </span>
      ) : null}
    </div>
  );
};
